import React, { useContext, useEffect, useReducer } from "react";
import {
  WmButton,
  WmChart,
  WmChartSlice,
  WmFlyout
} from "@watermarkinsights/ripple-react";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import { closeFlyout, focusButtonComponent } from "../../static/js/commons";
import { useIntl } from "react-intl";
import translate from "@/i18n/translate";
import { sendRequest } from "@/common/http";
import { decode } from "html-entities";

const defaultOrgCountData = {
  common_assessments: [],
  name: "",
  orgs_count: 0,
  uuid: ""
};
const defaultOpenDrillDownViewSllAction = {
  type: "",
  showLessMoreBtn: false,
  selectedAssessmentId: "",
  orgCountData: defaultOrgCountData,
  hierarchy: []
};
interface StudentAssessments {
  awaitingAssessment: number;
  awaitingSubmission: number;
  done: number;
}

interface CommonAssessments {
  name: string;
  orgs: string[];
  studentAssessments: StudentAssessments;
  totalActivities: number;
}

interface OrgCountData {
  commonAssessments: CommonAssessments[];
  name: string;
  orgsCount: number;
  uuid: string;
}

interface Hierarchy {
  commonAssessments: CommonAssessments;
  id: number;
}

interface OpenDrillDownViewSllState {
  hierarchy: Hierarchy[];
  orgCount: number;
  showTableView: boolean;
  showLessMoreBtn: boolean;
  selectedAssessmentId: string;
}

interface OpenDrillDownViewSllAction {
  type: string;
  showLessMoreBtn: boolean;
  selectedAssessmentId: string;
  orgCountData: OrgCountData;
  hierarchy: Hierarchy[];
}

const reducer = (
  state: OpenDrillDownViewSllState,
  action: OpenDrillDownViewSllAction
) => {
  switch (action.type) {
    case "setCommonAssessmentsData":
      return {
        ...state,
        hierarchy: action.hierarchy
      };
    case "setOrgCountData":
      return {
        ...state,
        orgCount: action.orgCountData.orgsCount,
        showTableView: true
      };
    case "setShowLessMoreBtn":
      return {
        ...state,
        showLessMoreBtn: !action.showLessMoreBtn,
        selectedAssessmentId: action.selectedAssessmentId
      };
    default:
      return state;
  }
};
export interface DrilldownViewProps {
  setIsDrilldownOpen: (arg0: boolean) => void;
  drilldownName?: string;
  subHeader?: string;
  title?: string;
  termUuid?: string;
  isVisible?: boolean;
  open?: boolean;
  id?: string;
}

export default function OpenDrillDownViewSll({
  setIsDrilldownOpen,
  drilldownName,
  subHeader,
  title,
  termUuid,
  isVisible = false,
  open = false,
  id = "drill-down-view"
}: DrilldownViewProps) {
  const { institution } = useContext<AppContextInterface>(AppContext);

  const initialState = {
    hierarchy: [],
    orgCount: 0,
    showTableView: false,
    showLessMoreBtn: false,
    selectedAssessmentId: ""
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!open || !termUuid) {
      return;
    }
    const applyResult = (result: OrgCountData) => {
      dispatch({
        ...defaultOpenDrillDownViewSllAction,
        type: "setOrgCountData",
        orgCountData: result
      });
      generateRandomCommonAssessmentId(result);
    };
    sendRequest(
      { uri: `/sll/activity_details/term/${termUuid}`, camelize: true },
      applyResult
    );
  }, [open, termUuid]);

  const getColumnName = () => {
    return [
      commonAssessmentVal,
      organizationLabel,
      totalActivities,
      studentAssessmentByStatus
    ];
  };

  const generateRandomCommonAssessmentId = (hierarchyData: OrgCountData) => {
    const finalHierarchyData = hierarchyData.commonAssessments?.map(
      (commonAssessments: CommonAssessments) => ({
        commonAssessments,
        id: getRandomId()
      })
    );

    dispatch({
      ...defaultOpenDrillDownViewSllAction,
      type: "setCommonAssessmentsData",
      hierarchy: finalHierarchyData
    });
  };

  function getRandomId() {
    return Math.random();
  }

  const getCommonAssessmentCount = () => {
    return state.hierarchy.length;
  };

  const hierarchyTableBody = () => {
    return (
      <div className="org-table">
        <Table>
          <TableHeader columns={getColumnName()} />
          <TableBody>
            {state.hierarchy.map((node: Hierarchy) => {
              let organizations = renderOrganizationsView(
                node?.commonAssessments.orgs,
                String(node?.id)
              );
              return (
                <tr className="datarow" key={node?.id}>
                  <td className="datacell">
                    {decode(node?.commonAssessments.name)}
                  </td>
                  <td className="datacell">{organizations}</td>
                  <td className="datacell">
                    {node?.commonAssessments.totalActivities}
                  </td>
                  <td className="datacell">
                    <RenderStudentAssessmentByTermChart
                      stuAssessment={node?.commonAssessments.studentAssessments}
                      labelText={decode(node?.commonAssessments.name)}
                    ></RenderStudentAssessmentByTermChart>
                  </td>
                </tr>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderOrganizationsView = (
    organizations: string[],
    assessmentId: string
  ) => {
    return organizations.length <= 1
      ? renderSingleOrganization(organizations)
      : renderMultipleOrganizations(organizations, assessmentId);
  };

  const renderSingleOrganization = (organizations: string[]) => {
    return organizations.map((name: string) => {
      return <div key={name}>{decode(name)}</div>;
    });
  };

  const renderMultipleOrganizations = (
    organizations: string[],
    assessmentId: string
  ) => {
    const sortedOrgs = organizations.sort().map((name: string) => {
      return name;
    });
    return !state.showLessMoreBtn ||
      assessmentId !== state.selectedAssessmentId ? (
      <div>
        <span>
          {translate("app.openDrillDownViewSll.orgNameLabel", {
            orgName: decode(sortedOrgs[0]),
            orgLength: sortedOrgs.length - 1
          })}
        </span>
        <div>
          <WmButton
            buttonType="textonly"
            id={`show-more-btn-${assessmentId}`}
            onClick={() =>
              showLessMoreBtn(
                state.showLessMoreBtn,
                `show-more-btn-${assessmentId}`,
                assessmentId
              )
            }
            key={`${sortedOrgs[0]}-data`}
          >
            {translate("app.openDrillDownViewSll.showMoreBtn")}
          </WmButton>
        </div>
      </div>
    ) : (
      <div>
        {sortedOrgs.map((name: string) => {
          return (
            <div key={`${name}-row`}>
              <span>{decode(name)}</span>
            </div>
          );
        })}
        <div>
          <WmButton
            buttonType="textonly"
            id={`show-less-btn-${assessmentId}`}
            onClick={() =>
              showLessMoreBtn(
                state.showLessMoreBtn,
                `show-less-btn-${assessmentId}`,
                assessmentId
              )
            }
          >
            {translate("app.openDrillDownViewSll.showLessBtn")}
          </WmButton>
        </div>
      </div>
    );
  };

  const showLessMoreBtn = (
    showLessMoreBtn: boolean,
    btnId: string,
    assessmentId: string
  ) => {
    dispatch({
      ...defaultOpenDrillDownViewSllAction,
      type: "setShowLessMoreBtn",
      showLessMoreBtn: showLessMoreBtn,
      selectedAssessmentId: assessmentId
    });
    setTimeout(() => focusButtonComponent(btnId), 300);
  };

  const CloseDrillDownView = () => {
    setIsDrilldownOpen(false);
    closeFlyout();
  };

  const DrillDownNameVal = (val: string) =>
    useIntl().formatMessage(
      {
        id: "app.assessmentProgressChart.studentAssessmentDrilldownName"
      },
      { termName: val }
    );

  const closeBtnText = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.closeBtnText"
  });
  const studentAssessmentByStatus = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.studentAssessmentsbyStatus"
  });
  const totalActivities = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.totalActivities"
  });
  const organizationLabel = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.organization"
  });
  const commonAssessmentVal = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.commonAssessmentVal"
  });

  const drillDownViewAriaLabel = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.drillDownViewAriaLabel"
  });

  return (
    <WmFlyout
      id={id}
      eyebrow={subHeader}
      heading={String(drilldownName || "No heading")}
      open={open}
      flyoutWidth="1224px"
      onWmFlyoutCloseTriggered={() => setIsDrilldownOpen(false)}
    >
        {state.showTableView && state.hierarchy ? (
          <section className="body">
            <div className="flex-row -justified -verticallycentered org-details-heading">
              <div id="organisation-details">
                <div className="title -title">
                  {title ?? (institution && institution.name)}
                </div>
                <div className="count">
                  {translate("app.openDrillDownViewSll.orgProgCount", {
                    orgCount: state.orgCount
                  })}
                </div>

                <div className="count">
                  {translate("app.openDrillDownViewSll.commonAssessmentCount", {
                    commonAssessmentCount: getCommonAssessmentCount()
                  })}
                </div>
              </div>

              <div id="chart-legends-area" aria-hidden="true" className="flex-row">
                <div className="legends-label">
                  {translate("app.openDrillDownViewSll.legend")}
                </div>
                <div className="legend-wrapper">
                  <div className="legend">
                    <div className="legend-item -drilldown-legends">
                      <div className="legend-color legend1 -legend-wrap"></div>
                      <div className="legend-text">
                        {translate(
                          "app.assessmentProgressChart.awaitingSubmission"
                        )}
                      </div>
                    </div>
                    <div className="legend-item -drilldown-legends">
                      <div className="legend-color legend2 -legend-wrap"></div>
                      <div className="legend-text">
                        {translate(
                          "app.assessmentProgressChart.awaitingAssessment"
                        )}
                      </div>
                    </div>
                    <div className="legend-item -drilldown-legends">
                      <div className="legend-color legend3 -legend-wrap"></div>
                      <div className="legend-text">{translate("app.done")}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {hierarchyTableBody()}
          </section>
        ) : (
          <div className="loader -halfscreen">
            <div className="content content-loader">
              {translate("app.loading")}
            </div>
          </div>
        )}
    </WmFlyout>
  );
}

const Table = (props: any) => {
  return <table className="data-table -shadow">{props.children}</table>;
};

const TableHeader = (props: any) => {
  return (
    <thead className="dataheader">
      <tr className="dataheaderrow">
        {props.columns.map((column: string, index: number) => {
          return (
            <th
              className="dataheadercell"
              data-th={column}
              key={`column-${index}`}
            >
              {column}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

const TableBody = (props: any) => {
  return <tbody className="databody">{props.children}</tbody>;
};

interface StuAssessment {
  awaitingAssessment: number;
  awaitingSubmission: number;
  done: number;
}
interface RenderStudentAssessmentByTermChartInterface {
  stuAssessment: StuAssessment;
  labelText: string;
}

const RenderStudentAssessmentByTermChart = (
  props: RenderStudentAssessmentByTermChartInterface
) => {
  const awaitingSubmission = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingSubmission"
  });

  const awaitingAssessment = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingAssessment"
  });

  const done = useIntl().formatMessage({
    id: "app.done"
  });

  function usePopTextByStatus(status: string, labelText: string) {
    return useIntl().formatMessage(
      {
        id: "app.assessmentProgressChart.popoverStatusText"
      },

      { statusLabel: status, labelText: labelText }
    );
  }

  function useGetLabelNameVal(labelText: string) {
    return useIntl().formatMessage(
      {
        id: "app.assessmentProgressChart.labelNameText"
      },

      { labelName: labelText }
    );
  }

  const total = Number(
    Object.values(props.stuAssessment).reduce(
      (newVal: number, oldVal: number) => (newVal = newVal + oldVal),
      0
    )
  );

  return (
    <WmChart
      chart-type="bar4"
      value-format="amount"
      show-legend="false"
      label={useGetLabelNameVal(props.labelText)}
    >
      <WmChartSlice
        legend={awaitingSubmission}
        amount={String(props.stuAssessment.awaitingSubmission)}
        popoverText={usePopTextByStatus(awaitingSubmission, props.labelText)}
        popover-title={props.stuAssessment.awaitingSubmission + "/" + total}
      />

      <WmChartSlice
        legend={awaitingAssessment}
        amount={String(props.stuAssessment.awaitingAssessment)}
        popoverText={usePopTextByStatus(awaitingAssessment, props.labelText)}
        popover-title={props.stuAssessment.awaitingAssessment + "/" + total}
      />

      <WmChartSlice
        legend={done}
        amount={String(props.stuAssessment.done)}
        popoverText={usePopTextByStatus(done, props.labelText)}
        popover-title={props.stuAssessment.done + "/" + total}
      />
    </WmChart>
  );
};
